// Unit conversion utilities for distance, pace, and speed

const KM_TO_MILES = 0.621371;
const MILES_TO_KM = 1.60934;

// Distance conversions
export const kmToMiles = (km) => km * KM_TO_MILES;
export const milesToKm = (miles) => miles * MILES_TO_KM;

// Format distance based on unit preference
export const formatDistance = (km, unit = 'km', decimals = 1) => {
  if (unit === 'mi') {
    return (km * KM_TO_MILES).toFixed(decimals);
  }
  return km.toFixed(decimals);
};

// Speed conversions (km/h to mph and vice versa)
export const kphToMph = (kph) => kph * KM_TO_MILES;
export const mphToKph = (mph) => mph * MILES_TO_KM;

// Calculate speed from pace (min:sec per km/mi -> km/h or mph)
export const paceToSpeed = (paceMinutes) => {
  if (!paceMinutes || paceMinutes <= 0) return 0;
  return 60 / paceMinutes;
};

// Convert pace between units (min/km to min/mi or vice versa)
export const convertPace = (paceMinutes, fromUnit, toUnit) => {
  if (fromUnit === toUnit) return paceMinutes;
  if (fromUnit === 'km' && toUnit === 'mi') {
    return paceMinutes * MILES_TO_KM;
  }
  return paceMinutes * KM_TO_MILES;
};

// Format pace as mm:ss
export const formatPace = (paceMinutes) => {
  if (!paceMinutes || paceMinutes <= 0) return '--:--';
  const minutes = Math.floor(paceMinutes);
  const seconds = Math.round((paceMinutes - minutes) * 60);
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Parse single pace string "mm:ss" to decimal minutes
export const parseSinglePace = (paceString) => {
  if (!paceString || typeof paceString !== 'string') return null;
  const cleanPace = paceString.replace(/\/km|\/mi/g, '').trim();
  const match = cleanPace.match(/^(\d+):(\d{2})$/);
  if (match) {
    return parseInt(match[1]) + parseInt(match[2]) / 60;
  }
  return null;
};

// Parse pace string - returns { start, end } for ranges or { start } for single
export const parsePace = (paceString) => {
  if (!paceString || typeof paceString !== 'string') return null;

  // Handle range like "5:00 - 5:30/km"
  if (paceString.includes(' - ')) {
    const [start, end] = paceString.split(' - ');
    const startPace = parseSinglePace(start);
    const endPace = parseSinglePace(end.replace(/\/km|\/mi/g, ''));
    if (startPace && endPace) {
      return { start: startPace, end: endPace, isRange: true };
    }
  }

  // Handle single pace
  const pace = parseSinglePace(paceString);
  if (pace) {
    return { start: pace, end: null, isRange: false };
  }
  return null;
};

// Format speed with units
export const formatSpeed = (kph, decimals = 1) => {
  return {
    kph: kph.toFixed(decimals),
    mph: (kph * KM_TO_MILES).toFixed(decimals)
  };
};

// Get pace and speed info from a workout
export const getWorkoutPaceInfo = (workout, unit = 'km') => {
  const result = {
    pacePerKm: null,
    pacePerMi: null,
    speedKph: null,
    speedMph: null
  };

  // Try to parse pace from pace_target
  if (workout.pace_target) {
    const paceData = parsePace(workout.pace_target);
    if (paceData) {
      if (paceData.isRange) {
        // Format as range: "5:00 - 5:30"
        result.pacePerKm = `${formatPace(paceData.start)} - ${formatPace(paceData.end)}`;
        result.pacePerMi = `${formatPace(paceData.start * MILES_TO_KM)} - ${formatPace(paceData.end * MILES_TO_KM)}`;
        // Calculate speed range (faster pace = higher speed, slower pace = lower speed)
        const speedFromStart = 60 / paceData.start;
        const speedFromEnd = 60 / paceData.end;
        result.speedKph = `${speedFromEnd.toFixed(1)} - ${speedFromStart.toFixed(1)}`;
        result.speedMph = `${(speedFromEnd * KM_TO_MILES).toFixed(1)} - ${(speedFromStart * KM_TO_MILES).toFixed(1)}`;
      } else {
        // Single pace value
        result.pacePerKm = formatPace(paceData.start);
        result.pacePerMi = formatPace(paceData.start * MILES_TO_KM);
        result.speedKph = (60 / paceData.start).toFixed(1);
        result.speedMph = ((60 / paceData.start) * KM_TO_MILES).toFixed(1);
      }
    }
  }

  return result;
};

// Local storage key for unit preference
const UNIT_PREFERENCE_KEY = 'aithlete_unit_preference';

// Get saved unit preference
export const getUnitPreference = () => {
  try {
    return localStorage.getItem(UNIT_PREFERENCE_KEY) || 'km';
  } catch {
    return 'km';
  }
};

// Save unit preference
export const setUnitPreference = (unit) => {
  try {
    localStorage.setItem(UNIT_PREFERENCE_KEY, unit);
  } catch {
    // Ignore storage errors
  }
};
